import styled from "@emotion/styled";
import { zIndex, fontWeight, borderRadius, duration } from "@atlas-int/tokens";
import { fontSizes } from "../typography";
import landingVideo from "../../assets/edited-video.mp4";
import { AnimatedScrollArrow } from "./ScrollArrow";
import NavigationBar from "../../../../components/navigation/ContentPageNav";

const LandingContainer = styled.div`
  height: 100vh;
  max-height: 100vh;
  box-sizing: border-box;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;

  @media (max-width: 600px) {
    height: auto;
    max-height: none;
  }
`;

const ScrollArrowContainer = styled.div`
  font-size: ${fontSizes.scrollIndicator};
  position: absolute;
  bottom: 1.5rem;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: ${zIndex.tooltip};

  @media (max-width: 900px) {
    bottom: 0.5rem;
  }

  @media (max-width: 600px) {
    position: static;
    left: auto;
    right: auto;
    bottom: auto;
  }
`;

const LandingContent = styled.div`
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-grow: 1;
  box-sizing: border-box;
  padding: 2rem 3rem 3rem 5rem;
  justify-content: space-between;
  align-items: stretch;
  gap: 4em;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5em;
    padding: 0 1rem 1rem 1rem;
  }

  @media (max-width: 600px) {
    height: auto;
    max-height: none;
  }
`;

const LandingTextContainer = styled.div`
  width: 50%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-bottom: 2em;
  padding-right: 0.5em;

  @media (max-width: 900px) {
    width: 90%;
    align-items: center;
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    padding-bottom: 0;
  }
`;

const Title = styled.h1`
  font-size: ${fontSizes.title};
  line-height: 1.1;
  margin: 0;
  padding: 0;
  font-weight: ${fontWeight.semibold};

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const Subtitle = styled(Title)`
  font-size: ${fontSizes.subtitle};
  padding-top: 0.5em;
  font-weight: ${fontWeight.normal};

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const Credits = styled.p`
  color: gray;
  font-style: italic;
  font-size: ${fontSizes.landingDescription};

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const Description = styled.p`
  margin-top: 0;
  line-height: 1.5;
  font-size: ${fontSizes.landingDescription};

  @media (max-width: 900px) {
    text-align: center;
  }
`;

const Button = styled.a`
  width: fit-content;
  margin-top: 0.75em;
  padding: 0.5em 1em;
  display: inline-block;
  font-family: inherit;
  cursor: pointer;
  background-color: rgb(46, 151, 191);
  color: white;
  text-decoration: none;
  border-radius: ${borderRadius.md};
  font-weight: ${fontWeight.semibold};
  font-size: ${fontSizes.landingDescription};
  transition: background-color ${duration.base} ease;

  &:hover {
    background-color: rgb(35, 130, 165);
    color: white;
  }

  @media (max-width: 900px) {
    margin-top: 0.25em;
  }
`;

const LandingGraphic = styled.div`
  width: 40%;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 900px) {
    width: 90%;
    flex: 1;
    min-height: 0;
  }
`;

export default function Landing() {
  return (
    <LandingContainer>
      <NavigationBar />
      <LandingContent>
        <LandingGraphic>
          <video
            style={{ height: "100%", width: "100%", objectFit: "contain" }}
            autoPlay
            muted
            loop
            playsInline
          >
            <source src={landingVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </LandingGraphic>
        <LandingTextContainer>
          <Title>
            When Numbers Diverge: <br />A Tale of Two Trading Partners
          </Title>
          <Subtitle>
            Building Harvard Growth Lab's International Trade Datasets
          </Subtitle>
          <Credits>
            By Sebastián Bustos, Ellie Jackson, David Torun, Brendan Leonard,
            Nil Tuzcu, Piotr Lukaszuk, Annie White, Ricardo Hausmann & Muhammed
            A. Yıldırım
          </Credits>
          <Description>
            Trade data powers decisions, from public policy to private
            investment. But the raw numbers often don't add up. When two
            countries trade, both report the same transaction, yet their figures
            often disagree. And over time, product classifications change,
            making it hard to compare trade data between countries. Here we
            explain how the Harvard Growth Lab builds a cleaner, more consistent
            trade dataset by: 1) Reconciling discrepancies between exporters and
            importers (mirroring); 2) Harmonizing product categories across
            classification changes using economically relevant weights. The end
            result is a publicly available trade dataset and a resource for
            researchers, policymakers, and anyone curious about how the world
            trades.
          </Description>
          <Button
            href="https://www.nature.com/articles/s41597-025-06488-2"
            target="_blank"
          >
            Read the Research Paper
          </Button>
        </LandingTextContainer>
      </LandingContent>
      <ScrollArrowContainer>
        <AnimatedScrollArrow
          text="Explore the methodology"
          hash="#introduction"
        />
      </ScrollArrowContainer>
    </LandingContainer>
  );
}
